import React, { useState, useRef, useEffect } from 'react';
import Image from 'next/image';
import { useDispatch, useSelector } from 'react-redux';
import { Avatar, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import format from 'date-fns/format';
import CopyToClipboard from 'react-copy-to-clipboard';

import { getRequest } from 'utils/api';
import { closeModal, openModal, setFooter } from 'features/utilsSlice';
import { getGigOwner } from 'utils/helpers';
import Toast from 'utils/toast';
import useProfileIds from 'hooks/useProfileIds';
import NoAvatarImage from 'public/images/no-avatar.svg';
import useModalOpen from 'hooks/useModalOpen';
import MessageAttachment from './MessageAttachment';
import TypingSection from './TypingSection';
import TransitionDialog from '../TransitionDialog';
import IconMainContainer from 'components/IconMainContainer';
import ProfileDialog from 'components/profile/ProfileDialog';
import AdvertiserProfileDialog from 'components/advertiserProfile/AdvertiserProfileDialog';
import styles from 'styles/components/gigs/Chat.module.scss';

const useStyles = makeStyles(theme => ({
  select: {
    color: '#5b00db',
    fontFamily: 'Avenir Medium',
    padding: 0,
  },

  root: {
    margin: 0,
    height: 'auto',
  },

  icon: {
    top: 6,
  },

  paper: {
    height: '90%',
    marginTop: 'auto',
    backgroundColor: '#f8f9fb',
    overflow: 'hidden',

    [theme.breakpoints.up('md')]: {
      height: '100%',
    },
  },
}));

const size = { width: 50, height: 50 };
const progressStyle = { marginTop: 50 };
const avatarFullSize = { width: '100%', height: '100%' };

const ChatContent = ({ gig = {} }) => {
  const [messages, setMessages] = useState([]);
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState({});
  const [domReady, setDomReady] = useState(false);

  const gigMessages = useSelector(state => state.gigs.gigMessages);

  const dispatch = useDispatch();
  const profileIds = useProfileIds();
  const endRef = useRef(null);

  const currentUser = useSelector(state => state.auth.currentUser);
  const isGigOwner = getGigOwner(currentUser, gig.user_id);

  const handleOpenProfile = () => {
    if (isGigOwner || gig.user.role === 'influencer') {
      dispatch(
        openModal({
          name: 'Profile',
          data: { username: isGigOwner ? gig.profile.username : gig.user.profile_id },
        })
      );
    } else {
      dispatch(openModal({ name: 'Advertiser Profile', data: { username: gig.user.username } }));
    }
  };

  const getMilestone = message => {
    if (message.conversation_type === 'Milestone') {
      return gig.milestones.find(m => m.id === message.conversation_id);
    }

    return null;
  };

  const removeFile = () => setFile(null);

  const getDisplayName = sender => {
    if (sender.type === 'Profile') {
      return profileIds?.includes(sender.id) ? 'Me' : sender.name;
    } else {
      return currentUser?.id === sender.id ? 'Me' : sender.name;
    }
  };

  const getMessageBody = message => {
    const linkRegex = /(https?:\/\/[^\s]+)/g;

    const match = message.match(linkRegex);

    if (!match) return message;

    const url = match[0];
    const parts = message.split(url);

    return (
      <>
        {parts[0] !== url && <span>{parts[0]}</span>}

        <CopyToClipboard
          text={url}
          onCopy={() => {
            Toast('success', 'Copied message link to clipboard!', {
              icon: <IconMainContainer src="/icons/notifications/thumb-up.svg" alt="Thumb Up" />,
            });
          }}
        >
          <span className="color-main">{url}</span>
        </CopyToClipboard>

        {parts[1] !== url && <span>{parts[1]}</span>}
      </>
    );
  };

  useEffect(() => {
    setIsLoading(prevState => ({ ...prevState, messages: true }));
    const endpoint = `gigs/${gig.id}/messages`;

    getRequest({
      endpoint,
    })
      .then(messages => {
        setMessages(messages);
        setIsLoading(prevState => ({ ...prevState, messages: false }));
      })
      .catch(() => setIsLoading(prevState => ({ ...prevState, messages: false })));
  }, [gig.id]);

  useEffect(() => {
    dispatch(setFooter(false));

    return () => dispatch(setFooter(true));
  }, [dispatch]);

  useEffect(() => setDomReady(true), []);

  useEffect(() => {
    const endDiv = endRef.current;

    if (domReady && endDiv && !isLoading.messages) {
      return (endDiv.scrollTop = endDiv.scrollHeight);
    }
  }, [messages, domReady, isLoading.messages]);

  useEffect(() => {
    const gigMessageId = gigMessages[gig.id]?.id;

    if (!gigMessageId) return;

    if (
      (gigMessageId > messages[messages.length - 1]?.id && !messages.includes(gigMessageId)) ||
      (gigMessageId && messages.length === 0)
    ) {
      setMessages(prevState => [...prevState, gigMessages[gig.id]]);
    }
  }, [gig.id, gigMessages, messages]);

  const styleAlert = { margin: '15px auto', marginLeft: '-8px' };

  return (
    <div className={styles.chatContainer}>
      <div>
        <div
          className={styles.header}
          style={{ justifyContent: 'center' }}
          onClick={handleOpenProfile}
        >
          <h2>{isGigOwner ? gig.profile.display_name : gig.user.display_name}</h2>
        </div>
      </div>

      <div className={styles.messages} style={{ height: 'calc(100% - 163px' }} ref={endRef}>
        {isLoading.messages && <CircularProgress color="primary" style={progressStyle} />}

        {messages.length === 0 && !isLoading.messages ? (
          <Alert style={styleAlert} severity="info">
            No messages
          </Alert>
        ) : (
          <>
            {messages.map(message => {
              const milestone = getMilestone(message);

              return (
                <div className={styles.message} key={message.id}>
                  <Avatar style={size} src={message.sender?.avatar_url}>
                    <NoAvatarImage style={avatarFullSize} />
                  </Avatar>

                  <div className={styles.messageBox}>
                    <h3 style={{ marginBottom: 10 }}>{getDisplayName(message.sender)}</h3>

                    {message.body && <p>{getMessageBody(message.body)}</p>}

                    {message.file && <MessageAttachment file={message.file} />}

                    {milestone && (
                      <div className={styles.milestone}>
                        <Image
                          width={16}
                          height={16}
                          src={milestone.network?.active_icon_url}
                          alt={milestone.network?.name}
                          unoptimized
                        />

                        <span className={styles.name}>{milestone.name}</span>
                      </div>
                    )}

                    <span className={styles.date}>
                      {format(new Date(message.created_at), 'MMM d, hh:mm a')}
                    </span>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>

      <TypingSection
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        removeFile={removeFile}
        file={file}
        setFile={setFile}
        gig={gig}
      />

      <ProfileDialog />
      <AdvertiserProfileDialog />
    </div>
  );
};

const ChatDialog = props => {
  const dispatch = useDispatch();
  const open = useModalOpen('Chat');
  const classes = useStyles();

  const handleClose = () => dispatch(closeModal('Chat'));

  return (
    <TransitionDialog
      open={open}
      handleClose={handleClose}
      paperProps={{ className: classes.paper }}
      dialogStyles={classes.paperFullScreenDialog}
      desktopDirection="up"
      direction="up"
      styles={styles}
      disablePortal
    >
      <ChatContent handleClose={handleClose} {...props} />
    </TransitionDialog>
  );
};

export default ChatDialog;
